import { useRequest } from 'ahooks';
import type { Options, Plugin, Service } from 'ahooks/lib/useRequest/src/types';
import { ErrorCode } from '@/constants/error';
import { Flow } from '@/constants/flow';
import { useBizState } from './use-biz-state';
import { useFlow } from './use-flow';

export function useCoreRequest<TData extends CoreRes, TParams extends unknown[]>(
  service: Service<TData, TParams>,
  options?: Omit<Options<TData, TParams>, 'onError'> & {
    // onError means if the return value is true, the error code has been handled, otherwise something went wrong will be executed
    onError?: (errorCode: ErrorCode) => boolean | void;
    skipCheck?: boolean;
  },
  plugins?: Plugin<TData, TParams>[],
) {
  const { googleVersion, setBizState } = useBizState();
  const { setFlow } = useFlow();

  return useRequest(
    service,
    {
      ...options,
      onBefore(params) {
        options?.onBefore?.(params);
      },
      onSuccess(res, params) {
        if (options?.skipCheck || ['SUCCESS', '200'].includes(String(res?.status))) {
          options?.onSuccess?.(res, params);
        } else {
          throw Error(res?.data?.code || res?.code);
        }
      },
      onError(e) {
        const eCode = e.message as ErrorCode;
        const isDeal = options?.onError?.(eCode);
        if (isDeal) return;

        const eMap = new Map<ErrorCode, Flow>([
          [ErrorCode.RequestTimeout, Flow.EXCEPTION_REQUEST_TIMEOUT],
          [ErrorCode.NetWorkError, Flow.EXCEPTION_NETWORK_ERROR],
          [ErrorCode.TroubleShooting, Flow.EXCEPTION_TROUBLESHOOTING],
          [ErrorCode.InvalidVerificationCode, Flow.EXCEPTION_EXPIRED_CODE],
          [ErrorCode.RequestLocked, Flow.EXCEPTION_REQUEST_BLOCKED],
        ]);

        // error handle...
        switch (eCode) {
          case ErrorCode.GoogleRecaptchaFailed:
            if (googleVersion === '3') setBizState({ googleVersion: '2' });
            else setFlow(Flow.EXCEPTION_SAFETY_TIPS);
            break;

          default:
            setFlow(eMap.get(eCode) ?? Flow.EXCEPTION_SOMETHING_WENT_WRONG, { state: { errorCode: eCode } });
            break;
        }
      },
    },
    plugins,
  );
}
